/**
 * Страны в админке: название по-русски вместо голого ISO-кода, флаг, поиск.
 *
 * НИ ОДНОГО НАЗВАНИЯ СТРАНЫ В КОДЕ. Названия даёт `Intl.DisplayNames` (CLDR
 * браузера), а набор кодов вычисляется перебором всех пар букв A–Z × A–Z:
 * код остаётся, если CLDR знает регион, код канонический (устаревшие `SU`,
 * `YU`, `ZR`… канонизация переводит в нынешние) и не служебный. Ручной
 * справочник разошёлся бы с CLDR при первом же переименовании страны.
 *
 * Хранимые значения не меняются: это по-прежнему ISO-коды (`DE`), меняется
 * только показ.
 */
const displayNamesCache = new Map();
/** Один экземпляр `DisplayNames` на локаль: создание дорогое, а зовут его в каждой строке таблицы. */
function displayNames(locale) {
    let dn = displayNamesCache.get(locale);
    if (!dn) {
        dn = new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' });
        displayNamesCache.set(locale, dn);
    }
    return dn;
}
/** `de` / ` DE ` → `DE`; не две латинские буквы — `null`. */
export function normalizeCountryCode(code) {
    if (code == null)
        return null;
    const c = code.trim().toUpperCase();
    return /^[A-Z]{2}$/.test(c) ? c : null;
}
/** Название региона из CLDR или `null`, если CLDR его не знает. */
function lookupName(code, locale) {
    try {
        const name = displayNames(locale).of(code);
        return name && name !== code ? name : null;
    }
    catch {
        return null;
    }
}
/**
 * Название страны: `DE` → «Германия». Название дают ТОЛЬКО коды из
 * `ALL_COUNTRY_CODES`. `DisplayNames` сам канонизирует устаревшие коды
 * (`UK` → «Великобритания», `SU` → «Россия», `DD` → «Германия»), и
 * невалидный для Meta `UK` выглядел бы как настоящая страна. Поэтому такой
 * код, служебный регион (`EU`), код НДС (`EL`) и мусор возвращаются как есть:
 * показать код лучше, чем выдать его за страну.
 */
export function countryName(code, locale = 'ru') {
    const c = normalizeCountryCode(code);
    if (c == null || !COUNTRY_SET.has(c))
        return code;
    return lookupName(c, locale) ?? code;
}
/** Входит ли код в `ALL_COUNTRY_CODES` (канонический код настоящей страны). */
export function isKnownCountryCode(code) {
    const c = normalizeCountryCode(code);
    return c != null && COUNTRY_SET.has(c);
}
/** Флаг из regional indicator symbols: `DE` → 🇩🇪. Не страна из списка (`UK`, `EU`) — пустая строка. */
export function countryFlag(code) {
    const c = normalizeCountryCode(code);
    if (c == null || !COUNTRY_SET.has(c))
        return '';
    const base = 0x1f1e6 - 65;
    return String.fromCodePoint(base + c.charCodeAt(0), base + c.charCodeAt(1));
}
/**
 * Регионы CLDR, которые не страны: макрорегионы и организации (`EU`, `EZ`,
 * `UN`) и «исключительно зарезервированные» территории без своей
 * ISO-страны (`AC`, `CP`, `CQ`, `DG`, `EA`, `IC`, `TA`). Meta их не принимает.
 */
const NON_COUNTRY_REGIONS = new Set([
    'AC',
    'CP',
    'CQ',
    'DG',
    'EA',
    'IC',
    'TA',
    'EU',
    'EZ',
    'UN',
]);
/**
 * Коды частного пользования ISO (`AA`, `QM`–`QZ`, `XA`–`XZ`, `ZZ`) — кроме
 * `XK` (Косово): его используют и CLDR, и Meta.
 */
function isPrivateUse(code) {
    if (code === 'XK')
        return false;
    if (code === 'AA' || code === 'ZZ')
        return true;
    return (code[0] === 'Q' && code[1] >= 'M') || code[0] === 'X';
}
/** Канонический ли код: устаревшие (`SU`, `YU`, `BU`, `UK`…) канонизация подменяет. */
function isCanonicalRegion(code) {
    try {
        return Intl.getCanonicalLocales(`und-${code}`)[0] === `und-${code}`;
    }
    catch {
        return false;
    }
}
function computeAllCountryCodes() {
    const out = [];
    for (let a = 65; a <= 90; a += 1) {
        for (let b = 65; b <= 90; b += 1) {
            const code = String.fromCharCode(a, b);
            if (NON_COUNTRY_REGIONS.has(code) || isPrivateUse(code))
                continue;
            if (lookupName(code, 'en') == null || !isCanonicalRegion(code))
                continue;
            out.push(code);
        }
    }
    return out;
}
/**
 * Все страны ISO 3166-1 alpha-2 (+ `XK`), по алфавиту кодов — 250 штук.
 * Считаются один раз при загрузке модуля: 676 вызовов `DisplayNames`, доли
 * миллисекунды.
 */
export const ALL_COUNTRY_CODES = computeAllCountryCodes();
/** Множество для `countryName`/`countryFlag`; обращаются к нему только после загрузки модуля. */
const COUNTRY_SET = new Set(ALL_COUNTRY_CODES);
// --- поиск -------------------------------------------------------------------
/**
 * Разговорные имена, которых нет в CLDR: оператор набирает «США», а CLDR
 * знает только «Соединенные Штаты». `UK` — не ISO, но его вставляют вместо `GB`.
 */
const ALIASES = {
    US: ['США', 'USA', 'Америка'],
    GB: ['UK', 'Англия'],
    AE: ['UAE', 'Эмираты'],
    KR: ['Южная Корея'],
    KP: ['Северная Корея'],
};
/** Ключ сравнения: без регистра, `ё` = `е`, без диакритики (Curaçao, Åland). */
function fold(s) {
    return s
        .trim()
        .toLocaleLowerCase('ru-RU')
        .replace(/ё/g, 'е')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');
}
let searchIndex = null;
function index() {
    searchIndex ??= ALL_COUNTRY_CODES.map((code) => {
        const ru = countryName(code, 'ru');
        const names = [ru, countryName(code, 'en'), ...(ALIASES[code] ?? [])].map(fold);
        return { code, ru, names };
    }).sort((a, b) => a.ru.localeCompare(b.ru, 'ru'));
    return searchIndex;
}
/** Ранг совпадения; меньше — выше в выдаче, `null` — не совпало. */
function rank(e, q) {
    if (e.code.toLowerCase() === q)
        return 0;
    if (e.names.some((n) => n.startsWith(q)))
        return 1;
    if (e.names.some((n) => n.split(/[\s\-–(’']+/).some((w) => w.startsWith(q))))
        return 2;
    if (e.names.some((n) => n.includes(q)))
        return 3;
    return null;
}
/**
 * Поиск страны по русскому и английскому названию и по коду, без учёта
 * регистра. Порядок: точный код, начало названия, начало слова, вхождение;
 * внутри ранга — по-русски по алфавиту. Пустой запрос — все страны по алфавиту.
 */
export function searchCountries(query, limit) {
    const q = fold(query);
    const all = index();
    const hits = q === ''
        ? all.map((e) => e.code)
        : all
            .map((e) => ({ e, r: rank(e, q) }))
            .filter((x) => x.r != null)
            .sort((a, b) => a.r - b.r)
            .map((x) => x.e.code);
    return limit != null ? hits.slice(0, limit) : hits;
}
/**
 * Один введённый токен → код страны: код (`de`), точное название по-русски
 * или по-английски (`Германия`, `germany`) или разговорное имя (`США`, `UK`).
 * Не узнали — `null`.
 */
export function resolveCountry(token) {
    const q = fold(token);
    if (q === '')
        return null;
    const entry = index().find((e) => e.code.toLowerCase() === q || e.names.includes(q));
    return entry?.code ?? null;
}
/**
 * Вставка списка (`DE, FR; it`, `DE FR IT`, по строке на страну) → коды.
 * `codes` — узнанные, без повторов; `rest` — неузнанные токены как были.
 */
export function parseCountryList(text) {
    const byDelimiter = text
        .split(/[,;\n\t]+/)
        .map((t) => t.trim())
        .filter((t) => t !== '');
    const tokens = byDelimiter.flatMap((t) => {
        if (resolveCountry(t) != null)
            return [t];
        // «DE FR IT» без запятых: делим по пробелам, только если ВСЕ части — коды
        const words = t.split(/\s+/);
        return words.length > 1 && words.every((w) => resolveCountry(w) != null) ? words : [t];
    });
    const codes = [];
    const rest = [];
    for (const t of tokens) {
        const code = resolveCountry(t);
        if (code == null)
            rest.push(t);
        else if (!codes.includes(code))
            codes.push(code);
    }
    return { codes, rest };
}
// --- списки ------------------------------------------------------------------
/** «Германия (DE)» — для подсказок и title, где важен и код. */
export function countryNameWithCode(code) {
    const name = countryName(code);
    return name === code ? code : `${name} (${code})`;
}
/**
 * Список стран словами: первые `max` названий + «и ещё K». `full` — весь
 * список с кодами, для `title`/подсказки.
 */
export function formatCountryList(codes, max = 5) {
    const names = codes.map((c) => countryName(c));
    const shown = names.slice(0, max).join(', ');
    const hidden = names.length - Math.min(names.length, max);
    return {
        text: hidden > 0 ? `${shown} и ещё ${hidden}` : shown,
        full: codes.map(countryNameWithCode).join(', '),
    };
}
/**
 * Список стран словами под бюджет символов: столько названий, сколько влезает
 * в `maxChars` вместе с хвостом «и ещё K» (но хотя бы одно). Для подписи,
 * которой отведено ~2 строки мелкого текста (пункт выпадающего списка), где
 * фиксированное «первые N» то обрезается, то оставляет пустое место. `full` —
 * весь список с кодами, для `title`.
 */
export function formatCountryListWithin(codes, maxChars = 90) {
    const names = codes.map((c) => countryName(c));
    let shown = Math.min(1, names.length);
    let len = names[0]?.length ?? 0;
    while (shown < names.length) {
        const nextLen = len + 2 + names[shown].length;
        const rest = names.length - shown - 1;
        const tail = rest > 0 ? ` и ещё ${rest}`.length : 0;
        if (nextLen + tail > maxChars)
            break;
        len = nextLen;
        shown += 1;
    }
    return formatCountryList(codes, shown);
}
//# sourceMappingURL=country.js.map